import type { JSX } from '@builder.io/qwik';
import {
  component$,
  useContext,
  useSignal,
  useVisibleTask$,
} from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import { AppDataContext } from '~/contexts/AppDataContext';
import type { ImageBlockAttsInterface } from '~/types/AppData/Blocks/ImageBlockInterface';
import {
  getImageCaption,
  getImageSrcAndSrcSet,
} from '~/utilities/image/image.utils';
import { ConditionalWrapper } from '../Utility/ConditionalWrapper/ConditionalWrapper';
import type { ImageWidths } from '~/types/AppData/PartialImageTypes';
import FigureWrapper from './FigureWrapper';

interface ImageProps extends ImageBlockAttsInterface {
  imageWidths: ImageWidths;
  captionClass?: string;
  loading?: 'eager' | 'lazy';
  imageClass?: string;
  wrapperClass?: string;
}

const imgClasses = cva('w-full', {
  variants: {
    isLoaded: {
      true: '',
      false: 'blur-[5px]',
    },
  },
});

export default component$<ImageProps>(
  ({
    alt,
    aspectRatio,
    path,
    caption,
    host: originalHost,
    credit,
    cropping,
    fileExtension,
    imageWidths,
    captionClass,
    loading = 'lazy',
    imageClass,
    wrapperClass,
    // TODO: Add these props in future
    // fullImageHtml, will need this in future
    // sizeType, will need this in future
  }): JSX.Element => {
    const {
      payloadToSSR: {
        propertySettings: { imagesCDNHost },
      },
    } = useContext(AppDataContext);
    const imageHost = originalHost || imagesCDNHost;

    const { smallSrc, mediumSrc, largeSrc, fallbackSrc, lowResSrc } =
      getImageSrcAndSrcSet(
        imageWidths,
        imageHost,
        `${path}.${fileExtension}`,
        cropping,
        aspectRatio
      );
    const isEagerLoad = loading === 'eager';
    const captionAndCredit = getImageCaption(credit, caption);
    const src = useSignal(isEagerLoad ? fallbackSrc : lowResSrc);
    const isLoaded = useSignal(isEagerLoad);

    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(() => {
      if (!isEagerLoad) {
        src.value = fallbackSrc;
        isLoaded.value = true;
      }
    });
    const loadClasses = imgClasses({ isLoaded: isLoaded.value });

    return (
      <ConditionalWrapper
        component={FigureWrapper}
        condition={!!captionAndCredit}
        props={{ captionClass, captionAndCredit }}
      >
        <div class={`overflow-hidden bg-lightGrey ${wrapperClass}`}>
          <picture>
            <source srcset={smallSrc} media='(max-width: 719px)' />
            <source srcset={mediumSrc} media='(max-width: 1079px)' />
            <source srcset={largeSrc} media='(min-width: 1080px)' />
            <img
              class={`${imageClass} w-full ${loadClasses}`}
              src={isEagerLoad ? fallbackSrc : src.value}
              alt={alt || caption || ''}
              title={alt || caption || ''}
              width={cropping?.width ?? undefined}
              height={cropping?.height ?? undefined}
            />
          </picture>
        </div>
      </ConditionalWrapper>
    );
  }
);
